/**
 * ContentEditSheet — V9.17 Sprint 3 Lot B
 *
 * Drawer latéral (desktop) / plein écran (mobile) pour éditer un contenu
 * depuis /admin/contenus. Réutilise les endpoints existants (curator, contrib,
 * news) via un adapter selon `kind`. Ne recrée AUCUN système d'édition.
 *
 * Features :
 *  - Chargement /api/admin/contents/item/{kind}/{id}
 *  - Édition champs communs (titre, résumé, image, catégorie, discipline,
 *    tags, homepage_visible, featured, pinned)
 *  - Détection de doublons éditoriaux (déterministe, à la demande)
 *  - Enrichissement IA à la demande (bouton "Suggérer avec IA")
 *  - Accepter / Modifier / Refuser suggestion champ par champ
 *  - Précédent / Suivant dans la liste filtrée
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { showError } from "@/lib/errors";
import {
  Save, Send, EyeOff, Archive, Star, Sparkles, Search, X, Check, ExternalLink,
  ChevronLeft, ChevronRight, AlertTriangle, Copy,
} from "lucide-react";

// Mappe kind → endpoints de mutation (source de vérité existante)
const MUTATION_ADAPTERS = {
  curator_item: {
    patch: (id, body) => api.patch(`/curator/items/${id}`, body),
    publish: (id) => api.post(`/curator/items/${id}/publish`, {}),
    unpublish: (id) => api.post(`/admin/entities/curator_item/${id}/unpublish`, {}),
    homepage: (id, on) => api.post(`/curator/items/${id}/homepage`, { homepage_visible: on }),
    archive: (id) => api.post(`/admin/entities/curator_item/${id}/archive`, {}),
  },
  user_article: {
    patch: (id, body) => api.patch(`/contrib/articles/${id}`, body),
    publish: (id) => api.post(`/admin/contrib/articles/${id}/publish`, {}),
    unpublish: (id) => api.post(`/admin/entities/user_article/${id}/unpublish`, {}),
    homepage: (id, on) => api.patch(`/contrib/articles/${id}`, { homepage_visible: on }),
    archive: (id) => api.post(`/admin/entities/user_article/${id}/archive`, {}),
  },
  news: {
    patch: (id, body) => api.patch(`/news/${id}`, body),
    publish: (id) => api.patch(`/news/${id}`, { published: true }),
    unpublish: (id) => api.patch(`/news/${id}`, { published: false }),
    homepage: () => Promise.reject(new Error("News : pas de champ homepage")),
    archive: (id) => api.post(`/admin/entities/news/${id}/archive`, {}),
  },
};

export function ContentEditSheet({
  open, onOpenChange, kind, itemId, list, onNavigate, onChanged,
}) {
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const [form, setForm] = useState({});
  const [expandedSugg, setExpandedSugg] = useState({}); // {suggId: overrideValue}

  const currentIndex = list?.findIndex((x) => x.kind === kind && x.id === itemId);
  const prev = currentIndex > 0 ? list[currentIndex - 1] : null;
  const next = currentIndex >= 0 && currentIndex < (list?.length || 0) - 1 ? list[currentIndex + 1] : null;

  const load = useCallback(async () => {
    if (!open || !kind || !itemId) return;
    setLoading(true);
    setDuplicates(null);
    try {
      const { data } = await api.get(`/admin/contents/item/${kind}/${itemId}`);
      setItem(data.item);
      const it = data.item || {};
      setForm({
        title: it.title || "",
        summary: it.summary || "",
        image_url: it.image_url || it.cover_url || "",
        url: it.url || it.source_url || "",
        category: it.category || "",
        discipline: it.discipline || "",
        tags: (it.hashtags || it.tags || []).join(", "),
        homepage_visible: !!it.homepage_visible,
        featured: !!it.featured,
        pinned: !!it.pinned,
      });
      setExpandedSugg({});
    } catch (e) { showError(e, { prefix: "Détail :" }); }
    finally { setLoading(false); }
  }, [open, kind, itemId]);

  useEffect(() => { load(); }, [load]);

  const adapter = MUTATION_ADAPTERS[kind];

  const saveDraft = async () => {
    if (!adapter) return;
    setBusy(true);
    try {
      const body = {
        title: form.title,
        summary: form.summary,
        category: form.category || null,
        discipline: form.discipline || null,
      };
      if (kind === "curator_item") {
        body.hashtags = (form.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
        body.image_url = form.image_url || null;
      } else {
        body.tags = (form.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
        body.cover_url = form.image_url || null;
      }
      await adapter.patch(itemId, body);
      toast.success("Modifications enregistrées.");
      onChanged?.();
      await load();
    } catch (e) { showError(e, { prefix: "Enregistrer :" }); }
    finally { setBusy(false); }
  };

  const doAction = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onChanged?.(); await load(); }
    catch (e) { showError(e); }
    finally { setBusy(false); }
  };

  const detectDup = async () => {
    setDupBusy(true);
    try {
      const { data } = await api.post(`/admin/contents/detect-duplicates/${kind}/${itemId}`);
      setDuplicates(data);
      const total = (data.certain?.length || 0) + (data.probable?.length || 0) + (data.to_check?.length || 0);
      toast.success(`${total} correspondance(s) trouvée(s).`);
    } catch (e) { showError(e, { prefix: "Doublons :" }); }
    finally { setDupBusy(false); }
  };

  const aiSuggest = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/admin/contents/ai-suggest/${kind}/${itemId}`);
      toast.success(`${data.suggestions?.length || 0} suggestion(s) IA générée(s).`);
      await load();
    } catch (e) { showError(e, { prefix: "IA :" }); }
    finally { setAiBusy(false); }
  };

  const resolveSugg = async (sugg, status, override) => {
    try {
      await api.post(`/admin/contents/ai-suggestions/${kind}/${itemId}/${sugg.id}`, {
        status,
        override_value: override === undefined ? null : override,
      });
      toast.success(status === "accepted" ? "Suggestion acceptée." : "Suggestion rejetée.");
      await load();
    } catch (e) { showError(e); }
  };

  if (!kind || !itemId) return null;

  const proposedSugg = (item?.ai_suggestions || []).filter((s) => s.status === "proposed");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="content-edit-sheet">
        <SheetHeader className="mb-3">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-base font-display">Édition — {kind}</SheetTitle>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={!prev} onClick={() => prev && onNavigate?.(prev)} data-testid="sheet-nav-prev" title="Précédent">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-[10px] text-muted-foreground font-mono">{currentIndex >= 0 ? `${currentIndex + 1}/${list?.length || 0}` : ""}</span>
              <Button size="sm" variant="ghost" disabled={!next} onClick={() => next && onNavigate?.(next)} data-testid="sheet-nav-next" title="Suivant">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <SheetDescription className="text-xs">Réutilise les endpoints existants. Les publications restent auditées.</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
        ) : !item ? (
          <div className="text-sm text-muted-foreground">Contenu introuvable.</div>
        ) : (
          <div className="space-y-3">
            {/* Champs édition */}
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sheet-input-title" />
            </div>
            <div>
              <Label>URL source</Label>
              <div className="flex items-center gap-2">
                <Input value={form.url} readOnly className="font-mono text-xs" />
                {form.url && <a href={form.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))]"><ExternalLink className="h-4 w-4" /></a>}
              </div>
            </div>
            <div>
              <Label>Résumé</Label>
              <Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="sheet-input-summary" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Catégorie</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="sheet-input-category" />
              </div>
              <div>
                <Label>Discipline</Label>
                <Input value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} data-testid="sheet-input-discipline" />
              </div>
            </div>
            <div>
              <Label>Tags (séparés par virgule)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="sheet-input-tags" />
            </div>
            <div>
              <Label>Image (URL)</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="sheet-input-image" />
              {form.image_url && <img src={form.image_url} alt="" className="mt-1 max-h-24 rounded border" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            </div>

            {/* Statut affichage */}
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <Badge variant="outline">status : {item.status || "—"}</Badge>
              {item.homepage_visible && <Badge className="bg-emerald-100 text-emerald-800">home visible</Badge>}
              {item.featured && <Badge className="bg-[hsl(var(--aito-lagoon))] text-black">featured</Badge>}
              {item.pinned && <Badge className="bg-[hsl(var(--aito-gold))] text-black">pinned</Badge>}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={saveDraft} disabled={busy} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="sheet-save"><Save className="h-3.5 w-3.5 mr-1" />Enregistrer</Button>
              {adapter?.publish && <Button size="sm" variant="outline" onClick={() => doAction(() => adapter.publish(itemId), "Publié")} disabled={busy} data-testid="sheet-publish"><Send className="h-3.5 w-3.5 mr-1" />Publier</Button>}
              {adapter?.unpublish && <Button size="sm" variant="outline" onClick={() => doAction(() => adapter.unpublish(itemId), "Dépublié")} disabled={busy} data-testid="sheet-unpublish"><EyeOff className="h-3.5 w-3.5 mr-1" />Dépublier</Button>}
              {adapter?.homepage && kind !== "news" && (
                <Button size="sm" variant="outline" onClick={() => doAction(() => adapter.homepage(itemId, !item.homepage_visible), item.homepage_visible ? "Retiré de la home" : "Ajouté à la home")} disabled={busy} data-testid="sheet-home-toggle">
                  <Star className="h-3.5 w-3.5 mr-1" />{item.homepage_visible ? "Retirer home" : "Mettre à la une"}
                </Button>
              )}
              {adapter?.archive && <Button size="sm" variant="outline" onClick={() => doAction(() => adapter.archive(itemId), "Archivé")} disabled={busy} data-testid="sheet-archive"><Archive className="h-3.5 w-3.5 mr-1" />Archiver</Button>}
            </div>

            <Separator />

            {/* Détection doublons */}
            <div>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Doublons éditoriaux</div>
                <Button size="sm" variant="ghost" onClick={detectDup} disabled={dupBusy} data-testid="sheet-detect-dup">
                  <Search className="h-3.5 w-3.5 mr-1" />Détecter
                </Button>
              </div>
              {duplicates && (
                <div className="mt-2 space-y-2 text-xs" data-testid="sheet-duplicates">
                  {["certain", "probable", "to_check"].map((lvl) => {
                    const arr = duplicates[lvl] || [];
                    if (arr.length === 0) return null;
                    const cls = lvl === "certain" ? "border-destructive bg-destructive/5" : lvl === "probable" ? "border-amber-300 bg-amber-50" : "border-muted bg-muted/30";
                    const label = lvl === "certain" ? "Certain" : lvl === "probable" ? "Probable" : "À vérifier";
                    return (
                      <Card key={lvl} className={`p-2 border ${cls}`}>
                        <div className="font-semibold flex items-center gap-1 mb-1"><Copy className="h-3 w-3" />{label} ({arr.length})</div>
                        <ul className="space-y-1">
                          {arr.map((d, i) => (
                            <li key={i} className="flex items-center justify-between gap-2">
                              <span className="truncate flex-1" title={d.title}>{d.title} <span className="text-muted-foreground">· score {d.score}</span></span>
                              {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))] shrink-0"><ExternalLink className="h-3 w-3" /></a>}
                            </li>
                          ))}
                        </ul>
                      </Card>
                    );
                  })}
                  {duplicates.certain?.length === 0 && duplicates.probable?.length === 0 && duplicates.to_check?.length === 0 && (
                    <div className="text-muted-foreground italic">Aucun doublon détecté.</div>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* IA suggestions */}
            <div>
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Suggestions IA</div>
                <Button size="sm" variant="outline" onClick={aiSuggest} disabled={aiBusy} data-testid="sheet-ai-suggest">
                  <Sparkles className="h-3.5 w-3.5 mr-1" />{aiBusy ? "Génération…" : "Suggérer avec IA"}
                </Button>
              </div>
              {proposedSugg.length === 0 ? (
                <div className="text-xs text-muted-foreground italic mt-2">Aucune suggestion active. L'IA n'est jamais appelée automatiquement.</div>
              ) : (
                <div className="mt-2 space-y-2" data-testid="sheet-ai-suggestions">
                  {proposedSugg.map((s) => (
                    <SuggestionRow key={s.id} sugg={s} onResolve={resolveSugg} expanded={expandedSugg[s.id]} setExpanded={(v) => setExpandedSugg((cur) => ({ ...cur, [s.id]: v }))} />
                  ))}
                </div>
              )}
            </div>

            <Separator />
            <div className="text-[10px] text-muted-foreground">
              id : <code className="bg-muted px-1 rounded">{item.id}</code> · créé {item.created_at?.slice(0, 10)} · maj {item.updated_at?.slice(0, 10)}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SuggestionRow({ sugg, onResolve, expanded, setExpanded }) {
  const [override, setOverride] = useState(sugg.value);
  const isList = Array.isArray(sugg.value);
  const displayValue = isList ? JSON.stringify(sugg.value, null, 0) : String(sugg.value ?? "");

  return (
    <Card className="p-2 border-l-4 border-[hsl(var(--aito-lagoon))]" data-testid={`sheet-sugg-${sugg.field}`}>
      <div className="flex items-center gap-2 mb-1">
        <Badge variant="outline" className="text-[10px]"><Sparkles className="h-2.5 w-2.5 mr-1" />{sugg.field}</Badge>
        <span className="text-[10px] text-muted-foreground">{sugg.provider}/{sugg.model}</span>
      </div>
      {!expanded ? (
        <div className="text-xs whitespace-pre-wrap line-clamp-3">{displayValue}</div>
      ) : (
        <Textarea rows={3} value={typeof override === "string" ? override : JSON.stringify(override)} onChange={(e) => {
          try { setOverride(isList ? JSON.parse(e.target.value) : e.target.value); }
          catch { setOverride(e.target.value); }
        }} className="text-xs font-mono" />
      )}
      <div className="mt-1.5 flex gap-1">
        <Button size="sm" variant="ghost" className="h-6 px-2 text-emerald-700 hover:bg-emerald-50" onClick={() => onResolve(sugg, "accepted", expanded ? override : undefined)} data-testid={`sheet-sugg-accept-${sugg.field}`}>
          <Check className="h-3 w-3 mr-1" />Accepter
        </Button>
        <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setExpanded(!expanded)} data-testid={`sheet-sugg-edit-${sugg.field}`}>
          {expanded ? "Masquer" : "Modifier"}
        </Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-destructive hover:bg-destructive/10" onClick={() => onResolve(sugg, "rejected")} data-testid={`sheet-sugg-reject-${sugg.field}`}>
          <X className="h-3 w-3 mr-1" />Refuser
        </Button>
      </div>
    </Card>
  );
}

export default ContentEditSheet;
